import { Box, Checkbox, Typography, useTheme } from '@mui/material';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import React, { FC, useContext, useEffect, useState } from 'react';
import { green, orange, red } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';

interface Props {
  primaryColor: string;
  secondaryColor: string;
  customFont: string;
  companyName: string;
  negativeText: string;
  positiveText: string;
  neutralText: string;
  title: string;
  onChange: (value: string) => void;
  question?: string;
}

const MoodBarometerIntro: FC<Props> = (props) => {
  const {
    primaryColor,
    secondaryColor,
    customFont,
    companyName,
    title,
    negativeText,
    positiveText,
    neutralText,
    question,
    onChange,
  } = props;

  const theme = useTheme();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation();
  const t = localContext ? localContext.t : globalT;
  const [selectedMood, setSelectedMood] = useState<string>('');

  useEffect(() => {
    onChange(selectedMood);
  }, [selectedMood]);

  return (
    <Box
      className="survey-question-step survey-question-step--trend"
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
    >
      <Typography
        className="survey-title survey-question-title"
        variant="h4"
        sx={{
          color: primaryColor,
          marginBottom: theme.spacing(4),
          fontWeight: 'bold',
          fontFamily: customFont,
        }}
      >
        {title}
      </Typography>
      <Typography
        className="survey-question-text"
        variant="h5"
        sx={{
          color: secondaryColor,
          mb: theme.spacing(1),
          whiteSpace: 'pre-wrap',
          fontFamily: customFont,
        }}
      >
        {question}
      </Typography>
      <Box className="survey-answer-options survey-answer-options--mood">
        <Box
          className="survey-answer-option survey-answer-option--positive"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(2),
            cursor: 'pointer',
          }}
          onClick={() => setSelectedMood('positive')}
        >
          <Checkbox
            className="survey-answer-checkbox"
            checked={selectedMood === 'positive'}
            onChange={() => setSelectedMood('positive')}
          />
          <SentimentSatisfiedAltIcon
            className="survey-answer-icon survey-answer-icon--positive"
            fontSize={'large'}
            sx={{ color: green[500] }}
          />
          <Typography
            className="survey-answer-label"
            variant="h5"
            sx={{
              color: secondaryColor,
              fontSize: '1.2rem',
              fontFamily: customFont,
            }}
          >
            {positiveText}
          </Typography>
        </Box>
        <Box
          className="survey-answer-option survey-answer-option--neutral"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(2),
            cursor: 'pointer',
          }}
          onClick={() => setSelectedMood('neutral')}
        >
          <Checkbox
            className="survey-answer-checkbox"
            checked={selectedMood === 'neutral'}
            onChange={() => setSelectedMood('neutral')}
          />
          <SentimentSatisfiedIcon
            className="survey-answer-icon survey-answer-icon--neutral"
            fontSize={'large'}
            sx={{ color: orange[500] }}
          />
          <Typography
            className="survey-answer-label"
            variant="h5"
            sx={{
              color: secondaryColor,
              fontSize: '1.2rem',
              fontFamily: customFont,
            }}
          >
            {neutralText}
          </Typography>
        </Box>
        <Box
          className="survey-answer-option survey-answer-option--negative"
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: theme.spacing(2),
            mt: theme.spacing(2),
            cursor: 'pointer',
          }}
          onClick={() => setSelectedMood('negative')}
        >
          <Checkbox
            className="survey-answer-checkbox"
            checked={selectedMood === 'negative'}
            onChange={() => setSelectedMood('negative')}
          />
          <SentimentVeryDissatisfiedIcon
            className="survey-answer-icon survey-answer-icon--negative"
            fontSize={'large'}
            sx={{ color: red[500] }}
          />
          <Typography
            className="survey-answer-label"
            variant="h5"
            sx={{
              color: secondaryColor,
              fontSize: '1.2rem',
              fontFamily: customFont,
            }}
          >
            {negativeText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MoodBarometerIntro;
